/**
 * Typed accessor API for entities.config.json — the universal entity configuration registry.
 *
 * Imports the JSON once at module init and exposes O(1) lookup functions keyed by entityTypeId.
 * All application code MUST use these accessors instead of directly importing the JSON or
 * duplicating entity metadata in switch/if blocks.
 *
 * Custom SPA entities (entityTypeId ≥ 128) are NOT in the static JSON — their stage prefixes
 * are derived dynamically via the DYNAMIC_{id}_STAGE convention.
 *
 * @see src/shared/config/entities.config.json — data file (edit here to change behaviour)
 * @see src/shared/types/entityConfig.ts        — TypeScript interfaces for the schema
 */

import configData from "./entities.config.json";
import type {
  EntityConfigFile,
  EntityConfig,
  UserEntityConfig,
  GlobalEntityDefaults,
  EntityDefaultCard,
  EntityDefaultQuery,
  EntityKanbanMapping,
  AppDefaults,
} from "../types/entityConfig";

// Cast once at module boundary — the JSON shape is validated by us against EntityConfigFile.
const _config = configData as unknown as EntityConfigFile;

// ── O(1) lookup maps built at module init ─────────────────────────────────────

const _entityById = new Map<number, EntityConfig>(_config.entities.map((e) => [e.entityTypeId, e]));

const _entityBySymbol = new Map<string, EntityConfig>(
  _config.entities.map((e) => [e.symbolCode, e]),
);

// ── Application defaults (cached reference) ──────────────────────────────────

const _appDefaults: AppDefaults = _config.appDefaults;

// ── Public accessor API ───────────────────────────────────────────────────────

/**
 * Returns application-wide tunable parameters from the `appDefaults` section of
 * entities.config.json. All numeric constants (batch limits, cache TTLs, retry
 * config, column caps, etc.) are sourced from this single location.
 */
export function getAppDefaults(): AppDefaults {
  return _appDefaults;
}

/**
 * Returns the full entity config for a known built-in entity type.
 * Returns null for custom SPA entities (entityTypeId ≥ 128) — those are runtime-discovered.
 */
export function getEntityConfig(entityTypeId: number): EntityConfig | null {
  return _entityById.get(entityTypeId) ?? null;
}

/**
 * Returns all 8 built-in entity configs in definition order.
 */
export function getAllEntityConfigs(): EntityConfig[] {
  return _config.entities;
}

/**
 * Returns the entity config matching a Bitrix24 symbolCode (e.g. 'DEAL', 'SMART_INVOICE').
 * Returns null if not found.
 */
export function getEntityConfigBySymbol(symbolCode: string): EntityConfig | null {
  return _entityBySymbol.get(symbolCode) ?? null;
}

/**
 * Returns the global defaults: baseSelect, fieldKeyTypeOverrides, defaultCardActions, defaultSort.
 */
export function getGlobalDefaults(): GlobalEntityDefaults {
  return _config.globalDefaults;
}

/**
 * Returns the complete fieldTypeMappings object — maps CardFieldType strings to entityTypeIds.
 * e.g. 'crm_deal' → 2, 'user' → 1044.
 */
export function getFieldTypeMappings(): Record<string, number> {
  return _config.fieldTypeMappings;
}

/**
 * Returns the user pseudo-entity config (entityTypeId = 1044).
 * Users are resolved via user.get, not crm.item.list.
 */
export function getUserEntityConfig(): UserEntityConfig {
  return _config.userEntity;
}

/**
 * Returns the default FlatCard field layout for the given entity type.
 * Falls back to a minimal generic layout for unknown entity types (custom SPAs).
 */
export function getEntityDefaultCard(entityTypeId: number): EntityDefaultCard {
  return (
    _entityById.get(entityTypeId)?.defaultCard ?? {
      headerFields: [
        {
          fieldKey: "title",
          label: "Item",
          type: "string",
          rowStyle: "simple",
          isEntityLink: true,
          hideLabel: true,
        },
        {
          fieldKey: "stageId",
          label: "Stage",
          type: "crm_status",
          rowStyle: "stage_color",
          hideLabel: true,
        },
      ],
      bodyFields: [
        {
          fieldKey: "contactId",
          label: "Contact",
          type: "crm_contact",
          rowStyle: "simple",
          displaySettings: { fieldType: "crm_contact", display: "title" },
        },
        {
          fieldKey: "companyId",
          label: "Company",
          type: "crm_company",
          rowStyle: "tag",
          tagColor: "blue",
          displaySettings: { fieldType: "crm_company", display: "title" },
        },
      ],
      footerFields: [
        {
          fieldKey: "assignedById",
          label: "Responsible",
          type: "user",
          rowStyle: "avatar",
          displaySettings: { fieldType: "user", display: "avatar_name" },
        },
        {
          fieldKey: "createdTime",
          label: "Created",
          type: "datetime",
          rowStyle: "simple",
          hideLabel: true,
          displaySettings: { fieldType: "datetime", format: "relative" },
        },
        {
          fieldKey: "updatedTime",
          label: "Updated",
          type: "datetime",
          rowStyle: "simple",
          displaySettings: { fieldType: "datetime", format: "relative" },
        },
      ],
      tooltipFields: [],
    }
  );
}

/**
 * Returns the default query configuration for the given entity type.
 * Falls back to a generic SPA config for unknown entity types.
 */
export function getEntityDefaultQuery(entityTypeId: number): EntityDefaultQuery {
  return (
    _entityById.get(entityTypeId)?.defaultQuery ?? {
      select: [
        "id",
        "title",
        "stageId",
        "categoryId",
        "assignedById",
        "createdTime",
        "updatedTime",
      ],
      searchFields: ["title"],
      sort: [{ field: "id", direction: "DESC" }],
    }
  );
}

/**
 * Returns the default kanban field mapping for the given entity type.
 * Returns undefined when the entity has no kanban mapping.
 * All 8 built-in entities return a defined mapping; generic SPA fallback returns a bitrix_pipeline mapping.
 */
export function getEntityKanbanMapping(entityTypeId: number): EntityKanbanMapping | undefined {
  const entry = _entityById.get(entityTypeId);
  if (entry) {
    return entry.defaultKanbanMapping ?? undefined;
  }
  return { kanbanType: "bitrix_pipeline", stageField: "stageId", categoryField: "categoryId" };
}

/**
 * Returns all built-in entities included in the CRM Navigator view,
 * sorted ascending by crmNavigator.sortOrder.
 */
export function getCrmNavigatorEntities(): EntityConfig[] {
  return _config.entities
    .filter((e) => e.crmNavigator.include)
    .sort((a, b) => a.crmNavigator.sortOrder - b.crmNavigator.sortOrder);
}

/**
 * Returns the entity's native Bitrix stage field key (e.g. 'stageId', 'statusId', 'typeId').
 * Returns null for unknown/custom SPA entities.
 */
export function getEntityNativeStageField(entityTypeId: number): string | null {
  return _entityById.get(entityTypeId)?.platform?.primaryStageField ?? null;
}
